import {useDispatch, useSelector} from 'react-redux';
import {useCallback, useEffect, useMemo, useRef} from 'react';

import {RootState} from '../../../store';
import {useDeployTask} from './useDeployTask';
import {addListener} from '../../../store/statusChecker';
import {TaskInfo} from '../../../types/metadata/TaskInfo';
import {toast} from '../../../base-components/StudioToast';
import {TestInitiateRequest} from '../../../api/test/TestInitiate';
import {Deployment} from '../../../types/deployment/DeploymentResponse';
import {InputSource} from '../../../types/deployment/InferenceInputSource';
import {AnalyticsParametersAnswer} from '../../../types/prompts/AnalyticsParameters';
import {
  useDeploy,
  TASK_STATUS,
  resetAllTasks,
  setDeployTask,
  setStreamingTask,
} from '../useDeploy';
import {EVENT_TYPE, getVideoStream, VideoStreamEvent} from '../VideoStream';
import Util from '../../../util';

export const useVideoStreamingTask = (projectId: string) => {
  const {start, stop, updateParameters} = useMemo(getVideoStream, []);
  const reduxDispatch = useDispatch();
  const [{streamingTask, deployTask}, dispatch] = useDeploy();

  const serverConfig = useSelector((state: RootState) => state.config);

  const {startDeploy, terminateDeploy} = useDeployTask(projectId);

  const monitorStatus = useCallback(
    (deploymentId, onMonitoringResponse) => {
      let wereParametersFound = false;
      reduxDispatch(
        addListener('GSP_DEPLOY', (status: TaskInfo) => {
          if (status.deploymentId !== deploymentId) {
            return;
          }
          dispatch(setDeployTask({monitoringResponse: status, deploymentId}));
          if (status.warnings.length > 0 && status.warnings[0]['detailMessage']) {
            toast.warn(`Warning: ${status.warnings[0]['detailMessage']}`);
          }
          if (status.failed) {
            dispatch(setStreamingTask({status: TASK_STATUS.ERROR}));
            if (status.errors.length > 0 && status.errors[0]['detailMessage']) {
              toast.error(`Streaming error: ${status.errors[0]['detailMessage']}`);
            } else {
              toast.error(`Streaming error. Please try again.`);
            }
            terminateDeploy(deploymentId);
          }
          if (Util.isRealNumberEqual(status.percentCompleted, 100) && status.finished) {
            dispatch(setDeployTask({status: TASK_STATUS.COMPLETE, deploymentId}));
          }
          if (!wereParametersFound && status.gspBoxOutputUrl) {
            wereParametersFound = true;
            onMonitoringResponse?.(status);
          }
        })
      );
    },
    [dispatch, reduxDispatch, terminateDeploy]
  );

  const cleanUpStream = useCallback(() => {
    if (streamingTask.status !== TASK_STATUS.IDLE) {
      stop();
      if (deployTask.status === TASK_STATUS.IN_PROGRESS) {
        terminateDeploy();
      }
    }
  }, [streamingTask.status, deployTask.status, terminateDeploy, stop]);

  const cleanUpStreamRef = useRef(cleanUpStream);

  useEffect(() => {
    cleanUpStreamRef.current = cleanUpStream;
  }, [cleanUpStream]);

  useEffect(
    function stopStreamIfWindowClosed() {
      window.addEventListener('beforeunload', cleanUpStream);
      return () => {
        window.removeEventListener('beforeunload', cleanUpStream);
      };
    },
    [cleanUpStream]
  );

  useEffect(function stopStreamIfUnmounted() {
    return () => {
      if (cleanUpStreamRef.current) {
        cleanUpStreamRef.current();
      }
    };
  }, []);

  useEffect(
    function updateRealtimeParameters() {
      if (streamingTask.status === TASK_STATUS.CONNECTED) {
        updateParameters(
          streamingTask.realtimePostProcessorParameters.concat(
            streamingTask.realtimePreProcessorParameters
          )
        );
      }
    },
    [
      streamingTask.status,
      streamingTask.realtimePostProcessorParameters,
      streamingTask.realtimePreProcessorParameters,
      updateParameters,
    ]
  );

  const runStreamingDeploy = async (
    config: TestInitiateRequest,
    onMonitoringResponse?: (monitoringResponse: TaskInfo) => void,
    onStartDeploy?: (deployment: Deployment) => void
  ) => {
    try {
      dispatch(setDeployTask({status: TASK_STATUS.IN_PROGRESS}));
      dispatch(setStreamingTask({status: TASK_STATUS.IN_PROGRESS}));
      const deploymentId = await startDeploy(config, onStartDeploy);
      dispatch(setDeployTask({status: TASK_STATUS.IN_PROGRESS, deploymentId}));
      monitorStatus(deploymentId, onMonitoringResponse);
    } catch (e) {
      stop();
      dispatch(resetAllTasks());
    }
  };

  const runStreamingTask = ({
    projectId,
    applicationId,
    spaId,
    schedulerId,
    name,
    description,
    limitedDuration,
    inferenceSource,
    environment,
    analyticsParameters,
    onStartDeploy,
    onFinishDeploy,
  }: {
    projectId: string;
    applicationId: string;
    spaId?: string;
    schedulerId?: string;
    name: string;
    description: string;
    limitedDuration: boolean;
    inferenceSource?: Exclude<InputSource, 'IMAGES'>;
    environment?: Record<string, string>;
    analyticsParameters?: AnalyticsParametersAnswer;
    onStartDeploy?: (deployment: Deployment) => void;
    onFinishDeploy?: () => void;
  }) => {
    const handleEvent = (event: VideoStreamEvent) => {
      switch (event.type) {
        case EVENT_TYPE.CONNECTING:
          dispatch(
            setStreamingTask({
              status: TASK_STATUS.IN_PROGRESS,
              receiveOnly: event.receiveOnly,
            })
          );
          break;
        case EVENT_TYPE.REGISTERED:
          dispatch(setStreamingTask({status: TASK_STATUS.READY}));
          const sessionId = event.sessionId;
          runStreamingDeploy(
            {
              webCameraVideoInferenceRequest: {
                projectId,
                applicationId,
                spaId,
                schedulerId,
                name,
                description,
                environment,
                analyticsParameters,
                streamingSessionId: sessionId,
              },
            },
            undefined,
            deployment => {
              if (event.monitorConfig) {
                event.monitorConfig.deploymentId = deployment.id;
              }
              if (onStartDeploy) {
                onStartDeploy(deployment);
              }
            }
          );
          break;
        case EVENT_TYPE.RECEIVE_ONLY_READY:
          dispatch(setStreamingTask({status: TASK_STATUS.READY}));
          break;
        case EVENT_TYPE.CONNECTED:
          dispatch(setStreamingTask({status: TASK_STATUS.CONNECTED}));
          break;
        case EVENT_TYPE.DATA:
          if (event.data) {
            dispatch(
              setStreamingTask({
                data: event.data,
              })
            );
          }
          break;
        case EVENT_TYPE.ERROR:
          dispatch(setStreamingTask({status: TASK_STATUS.ERROR}));
          if (event.message) {
            toast.error(`Streaming error: ${event.message}`);
          } else {
            toast.error(`Streaming error. Please try again.`);
          }
          terminateDeploy(event?.monitorConfig?.deploymentId);
          break;
        case EVENT_TYPE.STREAM_FINISHED:
          dispatch(setStreamingTask({status: TASK_STATUS.COMPLETE}));
          dispatch(
            setDeployTask({
              status: TASK_STATUS.COMPLETE,
              deploymentId: event?.monitorConfig?.deploymentId,
            })
          );
          terminateDeploy(event?.monitorConfig?.deploymentId);
          if (onFinishDeploy) {
            onFinishDeploy();
          }
          break;
        case EVENT_TYPE.USER_STREAM:
          dispatch(setStreamingTask({userStream: event.stream}));
          break;
        case EVENT_TYPE.REMOTE_STREAM:
          dispatch(setStreamingTask({remoteStream: event.stream}));
          break;
        default:
          break;
      }
    };

    if (inferenceSource === 'WEB_CAMERA') {
      start({
        projectId,
        serverConfig,
        receiveOnly: false,
        limitedDuration,
        internalSource: false,
        internalNetwork: false,
        monitorConfig: {
          monitorStream: true,
          projectId: projectId,
        },
        onEvent: handleEvent,
      });
    }
  };

  return [runStreamingTask, stop] as const;
};
